import React, { useRef, useState } from 'react';
import { Upload, X, File, AlertCircle } from 'lucide-react';

interface FileUploadProps {
  onFilesChange: (files: File[]) => void;
  maxFileSize?: number; // in MB
  maxTotalSize?: number; // in MB
  acceptedTypes?: string[];
  disabled?: boolean;
}

const FileUpload: React.FC<FileUploadProps> = ({
  onFilesChange,
  maxFileSize = 10, // 10MB per file
  maxTotalSize = 50, // 50MB total
  acceptedTypes = [],
  disabled = false
}) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [error, setError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getTotalSize = (files: File[]): number => {
    return files.reduce((total, file) => total + file.size, 0);
  };

  const validateFiles = (files: File[]): string | null => {
    // Check individual file sizes
    for (const file of files) {
      if (file.size > maxFileSize * 1024 * 1024) {
        return `File "${file.name}" exceeds maximum size of ${maxFileSize}MB`;
      }
    }

    // Check total size
    const currentTotal = getTotalSize(selectedFiles);
    const newTotal = getTotalSize(files);
    if (currentTotal + newTotal > maxTotalSize * 1024 * 1024) {
      return `Total file size would exceed ${maxTotalSize}MB`;
    }

    // Check file types if specified
    if (acceptedTypes.length > 0) {
      for (const file of files) {
        const extension = '.' + file.name.split('.').pop()?.toLowerCase();
        const mimeType = file.type;
        const isAccepted = acceptedTypes.some(
          type => type === extension || type === mimeType || type === '*'
        );
        if (!isAccepted) {
          return `File type "${extension}" is not accepted`;
        }
      }
    }

    return null;
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    const validationError = validateFiles(files);
    if (validationError) {
      setError(validationError);
      return;
    }

    const newFiles = [...selectedFiles, ...files];
    setSelectedFiles(newFiles);
    onFilesChange(newFiles);
    setError('');
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (index: number) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(newFiles);
    onFilesChange(newFiles);
    setError('');
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (disabled) return;

    const files = Array.from(event.dataTransfer.files);
    if (files.length === 0) return;

    const validationError = validateFiles(files);
    if (validationError) {
      setError(validationError);
      return;
    }

    const newFiles = [...selectedFiles, ...files];
    setSelectedFiles(newFiles);
    onFilesChange(newFiles);
    setError('');
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const totalSize = getTotalSize(selectedFiles);

  return (
    <div className="space-y-3">
      <div
        className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
          disabled
            ? 'border-slate-200 bg-slate-50 cursor-not-allowed'
            : 'border-slate-300 hover:border-pink-400 bg-slate-50 cursor-pointer'
        }`}
        onClick={() => !disabled && fileInputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileSelect}
          className="hidden"
          multiple
          disabled={disabled}
          accept={acceptedTypes.length > 0 ? acceptedTypes.join(',') : undefined}
        />
        <Upload className={`mx-auto mb-3 ${disabled ? 'text-slate-300' : 'text-pink-400'}`} size={32} />
        <p className={`text-sm font-medium ${disabled ? 'text-slate-400' : 'text-slate-600'}`}>
          Drag and drop files here or click to browse
        </p>
        <p className="text-xs text-slate-400 mt-1">
          {maxFileSize}MB per file, {maxTotalSize}MB total
        </p>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={16} />
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {selectedFiles.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm text-slate-500">
            <span>{selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} selected</span>
            <span>Total: {formatFileSize(totalSize)}</span>
          </div>
          <div className="space-y-2">
            {selectedFiles.map((file, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <File className="text-pink-400 flex-shrink-0" size={20} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-800 truncate">{file.name}</p>
                    <p className="text-xs text-slate-400">{formatFileSize(file.size)}</p>
                  </div>
                </div>
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => handleRemoveFile(index)}
                    className="p-1 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0 ml-2"
                  >
                    <X className="text-red-400" size={18} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
